// Theme core - resolving, applying and persisting the light/dark theme.
//
// Deliberately dependency-free and DOM-light: the same functions back both
// the render-blocking boot snippet (dist/theme.js, runs in <head> before
// paint) and the ThemeToggle behavior module (dist/main.js, runs on
// DOMContentLoaded). v1 had this logic twice - once in
// modules/ThemeToggle.js and once copy-pasted as an inline <script> in the
// CakePHP layout - and the two copies had already drifted apart.
//
// Division of labour:
//   - the boot snippet APPLIES the theme (sets [data-theme]) and persists
//     nothing;
//   - the toggle PERSISTS an explicit choice (localStorage + cookie) and
//     applies it.
// Nothing is written until the user actually picks a theme, so "no stored
// value" keeps meaning "follow the OS" for as long as the user never touched
// the switch. v1 persisted the resolved theme on the very first page load,
// which silently locked every visitor out of the system preference forever.

/** localStorage key and cookie name. Byte-for-byte the v1 key so existing visitors keep their choice. */
export const THEME_STORAGE_KEY = 'martinus-theme';

/** Fired on document after the theme changed. */
export const THEME_CHANGE_EVT = 'martinus:theme:change';

/** A resolved theme - what ends up in the [data-theme] attribute. */
export type Theme = 'light' | 'dark';

/**
 * What the user chose. `auto` (the default, stored as "no value") means the
 * theme follows the OS preference and keeps following it as it changes.
 */
export type ThemeMode = Theme | 'auto';

/** Detail of the THEME_CHANGE_EVT event. */
export interface ThemeChangeDetail {
  /** The theme now applied to the document. */
  theme: Theme;
  /** The mode it was resolved from (`auto` = derived from the OS). */
  mode: ThemeMode;
}

/** Options for applyTheme(). */
export interface ApplyThemeOptions {
  /**
   * Briefly set [data-theme-switching] on <html> so component transitions
   * (buttons, cards, inputs) don't animate their colors one by one during
   * the switch. Off in the boot snippet - there is nothing to animate before
   * first paint.
   */
  suppressTransitions?: boolean;
}

export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/** Attribute set on <html> for one frame while the theme is switching. */
export const THEME_SWITCHING_ATTR = 'data-theme-switching';

const THEME_ATTR = 'data-theme';
const COOKIE_MAX_AGE_DAYS = 365;

const isTheme = (value: unknown): value is Theme => value === 'light' || value === 'dark';

/** The OS-level preference. Defaults to light where matchMedia is missing. */
export const getSystemTheme = (): Theme =>
  window.matchMedia?.(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';

// localStorage throws in Safari private mode and wherever storage is
// disabled. v1 accessed it unguarded, so the whole module (and the inline
// snippet) died on the first read and the page stayed on the default theme.
const readStorage = (): string | null => {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStorage = (value: string): void => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, value);
  } catch {
    // Ignored - the cookie below is the persistence that actually matters,
    // since it is what the server reads to render [data-theme] up front.
  }
};

const readCookie = (): string | null => {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${THEME_STORAGE_KEY}=([^;]*)`),
  );

  return match ? decodeURIComponent(match[1] as string) : null;
};

const writeCookie = (value: string): void => {
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  // Secure on https so the preference isn't readable over a downgraded
  // connection; v1 set the cookie without it. max-age instead of v1's
  // expires - same lifetime, no Date formatting.
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';

  document.cookie = `${THEME_STORAGE_KEY}=${value};max-age=${maxAge};path=/;SameSite=Lax${secure}`;
};

/**
 * The mode the user explicitly chose, or `auto` when they never did.
 * localStorage wins; the cookie is a fallback so a cleared localStorage
 * doesn't lose the choice the server is still rendering from.
 */
export const getStoredMode = (): ThemeMode => {
  const stored = readStorage() ?? readCookie();

  return isTheme(stored) ? stored : 'auto';
};

/** Turn a mode into the theme to apply. */
export const resolveTheme = (mode: ThemeMode): Theme =>
  mode === 'auto' ? getSystemTheme() : mode;

/** The theme currently on the document, or null when nothing applied it yet. */
export const getAppliedTheme = (): Theme | null => {
  const applied = document.documentElement.getAttribute(THEME_ATTR);

  return isTheme(applied) ? applied : null;
};

/** The effective theme: what is applied, or what would be. */
export const getTheme = (): Theme => getAppliedTheme() ?? resolveTheme(getStoredMode());

/** Write the theme to the document. Persists nothing - see setTheme(). */
export const applyTheme = (theme: Theme, options: ApplyThemeOptions = {}): void => {
  const root = document.documentElement;

  if (options.suppressTransitions && root.getAttribute(THEME_ATTR) !== theme) {
    root.setAttribute(THEME_SWITCHING_ATTR, '');

    const release = (): void => root.removeAttribute(THEME_SWITCHING_ATTR);

    // Two frames: one for the attribute to take effect, one for the new
    // custom property values to be painted with transitions still off.
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(release));
    } else {
      window.setTimeout(release, 0);
    }
  }

  root.setAttribute(THEME_ATTR, theme);
};

/** Persist the mode (localStorage + cookie). `auto` clears the stored value. */
export const storeMode = (mode: ThemeMode): void => {
  if (mode === 'auto') {
    try {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } catch {
      // See writeStorage().
    }
    document.cookie = `${THEME_STORAGE_KEY}=;max-age=0;path=/;SameSite=Lax`;

    return;
  }

  writeStorage(mode);
  writeCookie(mode);
};

/**
 * Apply a mode, persist it and announce the change. This is what the toggle
 * calls, and the entry point for a consumer wiring up its own control.
 */
export const setTheme = (mode: ThemeMode, options: ApplyThemeOptions = {}): Theme => {
  const theme = resolveTheme(mode);

  storeMode(mode);
  applyTheme(theme, { suppressTransitions: true, ...options });

  document.dispatchEvent(
    new CustomEvent<ThemeChangeDetail>(THEME_CHANGE_EVT, {
      bubbles: true,
      detail: { theme, mode },
    }),
  );

  return theme;
};
